import React from 'react'
import { Modal, Button, Form, Row, Col } from 'react-bootstrap'
import { Trash3 } from 'react-bootstrap-icons'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { IMaskInput } from 'react-imask'
import Select from 'react-select'

import { Payment } from '../../../modules/store/dochubApi/dochubTypes'
import { IOption } from '../../../types/common/commonTypes'

type FormPayment = {
    date: string | null
    paySource: IOption | null
    amount: number
}

type FormValues = {
    payments: FormPayment[]
}

type Props = {
    isOpen: boolean
    closeModal: () => void
    paySources: IOption[]
    initialPayments?: Payment[]
    onSave: (data: Payment[]) => void
    totalPrice: number
}

export const ModalPayment = ({ isOpen, closeModal, paySources, initialPayments, onSave, totalPrice }: Props) => {
    const { control, handleSubmit, watch } = useForm<FormValues>({
        defaultValues: {
            payments: initialPayments
                ? initialPayments.map(payment => ({
                      date: payment.date,
                      paySource: payment.paySource
                          ? paySources.find(ps => ps.value === payment.paySource) || null
                          : null,
                      amount: payment.amount,
                  }))
                : [{ date: new Date().toLocaleDateString(), paySource: null, amount: 0 }],
        },
        mode: 'onSubmit',
    })

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'payments',
    })

    const payments = watch('payments')
    const paidAmount = payments.reduce((sum, payment) => {
        const amount = payment.amount || 0
        return sum + amount
    }, 0)
    const remainingAmount = totalPrice - paidAmount
    const [showValidation, setShowValidation] = React.useState(false)

    const handleAdd = () => {
        append({ date: new Date().toLocaleDateString(), paySource: null, amount: 0 })
    }

    const handleFormSubmit = (data: FormValues) => {
        if (remainingAmount < 0) {
            setShowValidation(true)
            return
        }

        const paymentsToSave = data.payments
            .map(payment => ({
                date: payment.date,
                paySource: payment.paySource?.value || null,
                amount: payment.amount,
            }))
            .filter(
                payment =>
                    // Удаляем объекты, где все три поля пустые/нулевые
                    payment.date || payment.paySource || payment.amount,
            )

        onSave(paymentsToSave)
        closeModal()
    }

    return (
        <Modal show={isOpen} onHide={closeModal} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Внесение оплаты</Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ backgroundColor: 'rgb(230, 230, 230)' }}>
                <Form noValidate>
                    {fields.map((field, index) => (
                        <Row className="mb-3" key={field.id}>
                            <Col xs={3}>
                                <Controller
                                    control={control}
                                    name={`payments.${index}.date`}
                                    rules={{
                                        validate: value => {
                                            if (!value) return true
                                            return value.length === 10 || 'Дата должна быть полностью заполнена'
                                        },
                                    }}
                                    render={({ field: { ref, onChange, value, ...rest }, fieldState: { error } }) => (
                                        <>
                                            <IMaskInput
                                                {...rest}
                                                value={value ?? ''}
                                                inputRef={ref}
                                                mask="00.00.0000"
                                                placeholder="Дата"
                                                onAccept={value => onChange(value || null)}
                                                className={`form-control ${error ? 'is-invalid' : ''}`}
                                            />
                                            {error && <div className="invalid-feedback">{error.message}</div>}
                                        </>
                                    )}
                                />
                            </Col>

                            <Col>
                                <Controller
                                    control={control}
                                    name={`payments.${index}.paySource`}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            options={paySources}
                                            placeholder="Источник оплаты"
                                            isClearable
                                        />
                                    )}
                                />
                            </Col>

                            <Col xs={3}>
                                <Controller
                                    control={control}
                                    name={`payments.${index}.amount`}
                                    render={({ field: { ref, onChange, value, ...rest } }) => (
                                        <IMaskInput
                                            {...rest}
                                            inputRef={ref}
                                            value={String(value)}
                                            mask={Number}
                                            scale={0}
                                            unmask="typed"
                                            thousandsSeparator=" "
                                            radix="."
                                            placeholder="Сумма"
                                            onAccept={value => onChange(Number(value))}
                                            className={`form-control`}
                                        />
                                    )}
                                />
                            </Col>

                            <Col xs="auto">
                                <Button variant="danger" onClick={() => remove(index)}>
                                    <Trash3 color="white" size={20} />
                                </Button>
                            </Col>
                        </Row>
                    ))}

                    <div className="d-flex justify-content-end mb-3">
                        <Button variant="outline-dark" onClick={handleAdd}>
                            + Добавить оплату
                        </Button>
                    </div>

                    <div>
                        Сумма к оплате - <span style={{ color: showValidation ? 'red' : 'inherit' }}>{totalPrice}</span>
                    </div>
                    <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex" style={{ gap: 20 }}>
                            <div>
                                Оплачено:{' '}
                                <span style={{ color: showValidation ? 'red' : 'inherit' }}>{paidAmount}</span>
                            </div>
                            <div>
                                Осталось доплатить:{' '}
                                <span style={{ color: showValidation ? 'red' : 'inherit' }}>{remainingAmount}</span>
                            </div>
                        </div>
                        <Button variant="primary" onClick={handleSubmit(handleFormSubmit)}>
                            Сохранить
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    )
}
